import { TERMS_LINKS } from '@avalara/bat-apps-shared/constants';
import { t, translate } from '@avalara/bat-apps-shared/i18n';
import type { Dispatch } from '@avalara/types/actions';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';
import { termsUrls } from '../../../shared/lib/constants';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { EXISTING_PAYMENT_METHOD_TOKEN } from '../../lib/constants';
import { getSignupValidationFieldNames, isFormIncomplete } from '../../lib/shared-signup';
import { updateProductAnalytics } from '../../lib/side-effects/analytics';
import { goToPreviousOfferStep } from '../../lib/side-effects/offer-flow';
import { purchaseOffer } from '../../lib/side-effects/offers';
import { selectIsNonAdmin, selectState } from '../../selectors';
import { isEmbeddedSelector } from '../../selectors/offer';
import { getOrderSummary, getPlanSpecificBillingTerms } from '../../selectors/order';
import { markFieldsForValidation } from '../../store/shared-signup';
import ExternalLink from '../external-link';
import { getBillingTerms } from './billing-terms';

type PaymentMethod = { Id: string } | null | undefined;

const getDisplayedTermsAndConditions = (
  termsAndConditions: Array<{ text: string; url: string }> | undefined,
  defaultTermsText: string,
) =>
  termsAndConditions?.length
    ? termsAndConditions
    : [
        {
          text: defaultTermsText,
          url: TERMS_LINKS.general,
        },
      ];

const getButtonText = (
  buyButtonTextOverride: string | undefined,
  isFreeTrial: boolean | undefined,
  labels: { finish: string; startFreeTrial: string },
) => buyButtonTextOverride || (isFreeTrial ? labels.startFreeTrial : labels.finish);

const shouldShowBackButton = (isEmbedded: boolean, isPaymentRequired: boolean, showEmbeddedSideContent?: boolean) =>
  isEmbedded && (isPaymentRequired || !showEmbeddedSideContent);

const shouldShowSubmitButton = (
  selectedPaymentMethod: PaymentMethod,
  isPaymentRequired: boolean,
  isHighRadiusCheckout: boolean,
) => !!(selectedPaymentMethod || !isPaymentRequired || isHighRadiusCheckout);

const purchaseOnSubmit = (
  dispatch: Dispatch,
  history: ReturnType<typeof useHistory>,
  isPaymentRequired: boolean,
  isHighRadiusCheckout: boolean,
  selectedPaymentMethod: PaymentMethod,
) => {
  if (!isPaymentRequired) {
    dispatch(purchaseOffer(history));
    return;
  }
  if (isHighRadiusCheckout) {
    dispatch(purchaseOffer(history, EXISTING_PAYMENT_METHOD_TOKEN));
    return;
  }
  if (selectedPaymentMethod) {
    dispatch(purchaseOffer(history, selectedPaymentMethod.Id));
  }
};

const BuyButton = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const {
    account: { isLoggedIn, selectedPaymentMethod },
    features,
    offer: {
      buyButtonTextOverride,
      isFreeTrial,
      isPaymentRequired,
      offerFlow,
      showEmbeddedSideContent,
      termsAndConditions,
    },
    order: { discountAdditionalLegalText },
    sharedSignup,
  } = useSelector(selectState);

  const { showBillingTermsInOrderSummary } = useSelector(getOrderSummary);

  const isHighRadiusCheckout = isLoggedIn && isPaymentRequired && !!features?.ENABLEHIGHRADIUSMIGRATION;

  const onSubmit = () => {
    if (!isLoggedIn) {
      dispatch(markFieldsForValidation(getSignupValidationFieldNames(sharedSignup)));
      if (isFormIncomplete(sharedSignup)) return;
    }

    dispatch(updateProductAnalytics('buy_now', true));
    purchaseOnSubmit(dispatch, history, isPaymentRequired, isHighRadiusCheckout, selectedPaymentMethod);
  };

  const goBack = () => dispatch(goToPreviousOfferStep(history));

  const displayedTermsAndConditions = getDisplayedTermsAndConditions(
    termsAndConditions,
    t('client.general.avalara-service-terms-and-conditions'),
  );

  const isNonAdmin = useSelector(selectIsNonAdmin);
  const disabled = isLoggedIn && isNonAdmin;
  const isEmbedded = useSelector(isEmbeddedSelector);
  const planSpecificBillingTerms = useSelector(getPlanSpecificBillingTerms);

  const showBillingTerms = !showBillingTermsInOrderSummary && isPaymentRequired;

  const buttonText = getButtonText(buyButtonTextOverride, isFreeTrial, {
    finish: t('client.offer.buy-button.finish'),
    startFreeTrial: t('client.offer.buy-button.start-your-free-trial'),
  });

  const getTermsAndConditions = displayedTermsAndConditions.map((tc, i) => (
    <Fragment key={tc.url}>
      {i > 0 && ', '}
      {i > 0 &&
        i === displayedTermsAndConditions.length - 1 &&
        t('client.offer.buy-button.terms-list-final-conjunction')}
      <ExternalLink href={tc.url} alwaysOpenInNewTab>
        {tc.text}
      </ExternalLink>
    </Fragment>
  ));

  const getAgreementTerms = () => {
    switch (offerFlow) {
      case 'ioss':
        return (
          <>
            <p data-ref='buy.ioss.agreement-terms'>
              {translate('client.offer.buy-button.ioss.agreement-terms', {
                buttonLabel: buttonText,
                termsAndConditions: getTermsAndConditions,
                buttonText: (chunks) => <span className='font-bold'>{chunks}</span>,
              })}
            </p>
            <p data-ref='buy.ioss.cancellation-terms' className='pad-top-md'>
              {translate('client.offer.buy-button.ioss.cancellation-terms', {
                cancellationUrl: termsUrls.cancellation,
                cancellationLink: (chunks) => (
                  <a href={termsUrls.cancellation} target='_blank' rel='noopener noreferrer'>
                    {chunks}
                  </a>
                ),
              })}
            </p>
          </>
        );
      case 'free-trial':
      case 'avatax-free-trial':
        return (
          <>
            <p data-ref='buy.free-trial.agreement-terms'>
              {translate('client.offer.buy-button.free-trial.agreement-terms', {
                buttonLabel: buttonText,
                termsAndConditions: getTermsAndConditions,
                buttonText: (chunks) => <span className='font-bold'>{chunks}</span>,
              })}
            </p>
          </>
        );
      default:
        return (
          <>
            <p>
              {translate('client.offer.buy-button.default.by-clicking', {
                buttonLabel: buttonText,
                buttonText: (chunks) => <span className='font-bold'>{chunks}</span>,
              })}
            </p>
            <br />
            <ul className='pad-left-lg'>
              <li data-ref='buy.default.agreement-terms'>
                {translate('client.disclaimer.you-accept-terms-and-conditions', {
                  termsAndConditions: getTermsAndConditions,
                })}
              </li>
              <li data-ref='buy.default.cancellation-terms'>
                {translate('client.offer.buy-button.default.cancellation-terms', {
                  cancellationUrl: termsUrls.cancellation,
                  cancellationLink: (chunks) => (
                    <ExternalLink href={termsUrls.cancellation} alwaysOpenInNewTab>
                      {chunks}
                    </ExternalLink>
                  ),
                })}
              </li>
            </ul>
          </>
        );
    }
  };

  return (
    <div className='pad-top-md'>
      <div className={isEmbedded ? 'text-right' : ''}>
        {shouldShowBackButton(isEmbedded, isPaymentRequired, showEmbeddedSideContent) && (
          <button
            type='button'
            className='secondary margin-right-lg'
            data-ref='buydot.buy-now.back-button'
            onClick={goBack}
          >
            {t('client.buttons.back')}
          </button>
        )}
        {shouldShowSubmitButton(selectedPaymentMethod, isPaymentRequired, isHighRadiusCheckout) && (
          <button
            type='button'
            id='submit-payment'
            className='primary'
            data-ref='buydot.buy-now.buy-button'
            disabled={disabled}
            onClick={onSubmit}
          >
            {buttonText}
          </button>
        )}
      </div>

      <div className='margin-top-sm'>
        <div className={isEmbedded ? 'text-right' : ''}>{offerFlow && getAgreementTerms()}</div>

        {showBillingTerms && offerFlow && (
          <div>
            {getBillingTerms(planSpecificBillingTerms, offerFlow).map((term, i) => (
              <p
                key={`billingTerm${
                  // biome-ignore lint/suspicious/noArrayIndexKey: what else to use as index here?
                  i
                }`}
                className='pad-top-sm'
                data-ref={`buy-button-billing-terms.${offerFlow}.term-${i}`}
              >
                {term}
              </p>
            ))}
          </div>
        )}

        {discountAdditionalLegalText && <p className='margin-top-sm'>{discountAdditionalLegalText}</p>}
      </div>
    </div>
  );
};

export default BuyButton;
